import React, { useState, useEffect } from 'react';
import { Button, Drawer, Flex, Form, Input, message, Modal, Radio, Select } from 'antd';
import { StaffInfo } from '@/types/staff';
import { PHONE_REGEX } from '@/utils/validate';
import { Gender } from '../utils';
import { SourceInfo, UnassignedClueList } from '@/types/clue';
import { IStudioInfo } from '@/types/studio';
import { EmploymentStatus } from '@/types/common';
import { getStaffList } from '@/services/staff';
import { addClue, AddClueResponse, updateClue } from '@/services/clue';
import { useMerchantConfig } from '@/hooks/useMerchantConfig';
import { USER_TYPE_LIST } from '@/constants/user';
const { TextArea } = Input;

export type ClueCardSuccessResult = {
  lead_id?: number;
};

function extractLeadIdFromResponse(res: AddClueResponse): number | undefined {
  if (typeof res.lead_id === 'number' && res.lead_id > 0) {
    return res.lead_id;
  }
  const firstItem = res.list?.[0];
  if (!firstItem) {
    return undefined;
  }
  if (typeof firstItem.lead_id === 'number' && firstItem.lead_id > 0) {
    return firstItem.lead_id;
  }
  if (typeof firstItem.id === 'number' && firstItem.id > 0) {
    return firstItem.id;
  }
  return undefined;
}

interface IProps {
  open: boolean;
  data?: UnassignedClueList;
  sourceList: SourceInfo[];
  storeList: IStudioInfo[];
  onClose: () => void;
  embedded?: boolean;
  phoneRequired?: boolean;
  defaultPhoneNumber?: string;
  submitText?: string;
  cancelText?: string;
  onSuccess?: (result: ClueCardSuccessResult) => void;
  onFormDirtyChange?: (dirty: boolean) => void;
  embeddedFooterFixed?: boolean;
  embeddedClassNames?: {
    layout?: string;
    scroll?: string;
    footer?: string;
  };
}

export default function DrawerAddClueCard({
  open,
  data,
  sourceList,
  storeList,
  onClose,
  embedded = false,
  phoneRequired = false,
  defaultPhoneNumber,
  submitText = '确认',
  cancelText = '取消',
  onSuccess,
  onFormDirtyChange,
  embeddedFooterFixed = false,
  embeddedClassNames,
}: IProps) {
  const isUpdate = !!data?.lead_id;
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [staffList, setStaffList] = useState<StaffInfo[]>([]);
  const merchantConfig = useMerchantConfig();

  // 监听open变化，当抽屉打开时重置表单
  useEffect(() => {
    if (open) {
      form.resetFields();
      setStaffList([]);
    }
  }, [open]);

  // 当 data 变化时设置表单初始值
  useEffect(() => {
    if (open && data?.lead_id) {
      form.setFieldsValue({
        name: data.name,
        phone_number: data.phone_number,
        wx_number: data.wx_number,
        source_id: data.source_id,
        gender: data.gender === 1 ? 'MALE' : data.gender === 2 ? 'FEMALE' : undefined,
        remark: data.remark,
      });
    } else if (open && defaultPhoneNumber) {
      form.setFieldsValue({
        phone_number: defaultPhoneNumber,
      });
    }
  }, [open, data, defaultPhoneNumber, form]);

  // 请求员工列表
  async function requestStaffList(storeId: number) {
    try {
      setLoading(true);
      const res = await getStaffList({
        status: EmploymentStatus.EMPLOYED,
        store_id: storeId,
        page_size: 100,
      });
      setStaffList(res?.list || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  async function onFinish() {
    try {
      form.submit();
      await form.validateFields();
      const values = form.getFieldsValue();

      // 检查联系方式：手机号和微信号二选一
      const phoneNumber = values.phone_number?.trim();
      const wxNumber = values.wx_number?.trim();

      if (phoneRequired && !phoneNumber) {
        message.error('请输入线索电话');
        return;
      }

      // 如果两个都没填写，提示错误并阻止提交
      if (!phoneNumber && !wxNumber) {
        message.error('线索未填写联系方式，手机号和微信号二选一');
        return;
      }

      // 如果只填写了微信号，没有手机号，弹出确认对话框
      if (!phoneRequired && !phoneNumber && wxNumber) {
        return new Promise<void>((resolve) => {
          Modal.confirm({
            title: '提示',
            content:
              '线索没有填写手机号，不会自动创建关联用户，用户登录微信小程序时无法和该线索关联，是否继续',
            onOk: async () => {
              try {
                // 确保名称字段去除前后空格
                if (values.name) {
                  values.name = values.name.trim();
                }

                const payload = {
                  ...values,
                  // source_id: values.source_id === 'MEITUAN' ? 1 : 2,
                  gender: values.gender === 'FEMALE' ? 2 : 1,
                  merchant_id: Number(merchantConfig?.merchant_id),
                  lead_id: isUpdate ? data?.lead_id : undefined,
                };
                setConfirmLoading(true);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const res = await (isUpdate ? updateClue : addClue)(payload);

                setConfirmLoading(false);
                message.success(isUpdate ? '更新成功' : res.message);
                if (onSuccess) {
                  onSuccess({ lead_id: extractLeadIdFromResponse(res) });
                } else {
                  onClose();
                }
                resolve();
              } catch (error) {
                // console.log('Validation failed', error);
                setConfirmLoading(false);
                resolve();
              }
            },
            onCancel: () => {
              resolve();
            },
          });
        });
      }

      // 确保名称字段去除前后空格
      if (values.name) {
        values.name = values.name.trim();
      }

      const payload = {
        ...values,
        // source_id: values.source_id === 'MEITUAN' ? 1 : 2,
        gender: values.gender === 'FEMALE' ? 2 : 1,
        merchant_id: Number(merchantConfig?.merchant_id),
        lead_id: isUpdate ? data?.lead_id : undefined,
      };
      setConfirmLoading(true);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const res = await (isUpdate ? updateClue : addClue)(payload);

      setConfirmLoading(false);
      message.success(isUpdate ? '更新成功' : res.message);
      if (onSuccess) {
        onSuccess({ lead_id: extractLeadIdFromResponse(res) });
      } else {
        onClose();
      }
    } catch (error) {
      // console.log('Validation failed', error);
    } finally {
      setConfirmLoading(false);
    }
  }

  const footer = (
    <Flex justify="end" gap="small">
      <Button onClick={onClose}>{cancelText}</Button>
      <Button onClick={onFinish} type="primary" loading={confirmLoading}>
        {submitText}
      </Button>
    </Flex>
  );

  const formContent = (
    <Form
      layout="horizontal"
      labelCol={{ span: embedded ? 6 : 7 }}
      wrapperCol={{ span: embedded ? 16 : 14 }}
      form={form}
      initialValues={{
        ...data,
      }}
      onValuesChange={() => {
        onFormDirtyChange?.(true);
      }}
    >
      <Form.Item
        name="name"
        label="线索名称"
        rules={[
          { required: true, message: '请输入线索名称' },
          {
            validator: (_, value) => {
              if (value && value.trim() === '') {
                return Promise.reject('线索名称不能只包含空格');
              }
              return Promise.resolve();
            },
          },
        ]}
        normalize={(value) => value && value.trim()}
      >
        <Input placeholder="请输入" />
      </Form.Item>

      <Form.Item
        name="phone_number"
        label="线索电话"
        rules={[
          ...(phoneRequired ? [{ required: true, message: '请输入线索电话' }] : []),
          { pattern: PHONE_REGEX, message: '手机号格式错误' },
        ]}
      >
        <Input placeholder="请输入线索电话" />
      </Form.Item>

      <Form.Item name="wx_number" label="线索微信">
        <Input placeholder="请输入线索微信" />
      </Form.Item>

      <Form.Item
        label="线索来源"
        name="source_id"
        rules={[{ required: true, message: '请选择线索来源' }]}
      >
        <Select options={sourceList} placeholder="请选择线索来源" />
      </Form.Item>
      <Form.Item
        label="用户类型"
        name="user_type"
        rules={[{ required: true, message: '请选择用户类型' }]}
      >
        <Select options={USER_TYPE_LIST} placeholder="请选择用户类型" />
      </Form.Item>
      <Form.Item name="gender" label="性别" rules={[{ required: true, message: '请选择性别' }]}>
        <Radio.Group name="radiogroup" options={Gender} />
      </Form.Item>
      {!isUpdate && storeList.length > 0 && (
        <Form.Item
          label="负责门店"
          name="store_id"
          rules={[{ required: true, message: '请选负责门店' }]}
        >
          <Select
            style={{ width: '100%' }}
            placeholder="请选择工作门店"
            options={storeList.map((item) => ({
              label: item.name,
              value: item.id,
              store_id: item.id,
            }))}
            onChange={async (value) => {
              await requestStaffList(value);
            }}
          />
        </Form.Item>
      )}
      {!isUpdate && storeList.length > 0 && (
        <Form.Item
          label="负责人"
          name="admin_id"
          rules={[{ required: true, message: '请选负责人' }]}
        >
          <Select
            options={staffList.map((item) => ({
              label: item.username,
              value: item.id,
              admin_id: item.id,
            }))}
            placeholder="请选择负责人"
          />
        </Form.Item>
      )}
      <Form.Item label="备注" name="remark">
        <TextArea placeholder="请输入备注" />
      </Form.Item>
    </Form>
  );

  if (embedded) {
    if (!open) {
      return null;
    }
    if (embeddedFooterFixed) {
      return (
        <div className={embeddedClassNames?.layout}>
          <div className={embeddedClassNames?.scroll}>{formContent}</div>
          <div className={embeddedClassNames?.footer}>{footer}</div>
        </div>
      );
    }
    return (
      <div>
        {formContent}
        <div style={{ marginTop: 24 }}>{footer}</div>
      </div>
    );
  }

  return (
    <Drawer
      title={isUpdate ? '编辑线索' : '录入线索'}
      width={500}
      onClose={onClose}
      open={open}
      styles={{
        body: {
          paddingBottom: 80,
        },
      }}
      footer={footer}
      afterOpenChange={(visible) => {
        if (!visible) {
          form.resetFields();
        }
      }}
    >
      {formContent}
    </Drawer>
  );
}
